import React from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Section from '../components/ui/Section';
import Button from '../components/ui/Button';
import SEOHelmet from '../seo/SEOHelmet';
import { useLanguage } from '../i18n/LanguageContext';
import { LanguageCode } from '../i18n/translations';
import HeaderBannerComponent from '../components/ui/HeaderBanner';

// Definizione di tipi per i contenuti localizzati
type LocalizedContent = {
  [key in LanguageCode]: string;
};

interface CityData {
  name: string;
  region: string;
  image: string;
  description: LocalizedContent | string;
  attractions: {
    title: LocalizedContent | string;
    content: LocalizedContent | string;
  }[];
  surroundings: {
    title: LocalizedContent | string;
    content: LocalizedContent | string;
  }[];
}

// Database delle città con i relativi contenuti
const cityDatabase: Record<string, CityData> = {
  firenze: {
    name: 'Firenze',
    region: 'Toscana',
    image: 'https://images.unsplash.com/photo-1534445867742-43195f401b6c?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
    description: {
      it: 'Firenze è senza dubbio una delle città più belle d\'Italia, le sue strade sono ricche di storia, arte e cultura. A Firenze troviamo i musei tra i più belli e importanti del mondo, palazzi storici, sculture e tanto altro. Passeggiare tra le vie di Firenze è una vera e propria esperienza e il centro storico si presta molto bene per essere visitato a piedi, godendovi così ancora di più l\'atmosfera.',
      en: 'Florence is undoubtedly one of the most beautiful cities in Italy, its streets are rich in history, art and culture. In Florence we find some of the most beautiful and important museums in the world, historic palaces, sculptures and much more. Walking through the streets of Florence is a real experience and the historic center is very well suited to be visited on foot, thus enjoying the atmosphere even more.'
    },
    attractions: [
      {
        title: {
          it: 'Duomo, Cupola del Brunelleschi, Campanile di Giotto e Battistero di San Giovanni',
          en: 'Duomo, Brunelleschi\'s Dome, Giotto\'s Bell Tower and Baptistery of San Giovanni'
        },
        content: {
          it: 'Si tratta di una delle attrazioni più visitate della città. Il nome completo è Cattedrale di Santa Maria del Fiore, ed è la quarta chiesa europea per grandezza, con le sue tre navate, le facciate esterne in marmo bianco e verde e i suoi interni spogli di arredi ma dominati da un ciclo di affreschi monumentali realizzati da Giorgio Vasari e Federico Zuccari, tra il 1572 e il 1579.\nIl Duomo di Firenze si articola in più parti e uno dei suoi punti forti è sicuramente la cupola del Brunelleschi: con la sua copertura in muratura, suddivisa in otto spicchi, la palla dorata che sorregge la croce in cima e la Lanterna, posta alla sommità, completata dopo la morte dell\'artista.\nPoi abbiamo il meraviglioso Campanile di Giotto, considerato il campanile più bello d\'Italia, dalla cui cima di gode di una meravigliosa vista su tutta Firenze. Con un\'altezza di quasi 85 m, rappresenta l\'architettura gotica fiorentina del Trecento. Il progetto del campanile fu iniziato nel 1334 da Giotto, che purtroppo non riuscì a vederlo completo: morì infatti nel 1337, quando era stata realizzata solo la prima parte del progetto! La costruzione venne quindi portata avanti da Andrea Pisani, seguendo alla lettera le istruzioni di Giotto.\nQuarta e ultima bellezza del Duomo di Firenze: il Battistero di San Giovanni. Situato davanti all\'ingresso della Cattedrale, si tratta di una delle più antiche chiese di Firenze.',
          en: 'This is one of the most visited attractions in the city. The full name is Cathedral of Santa Maria del Fiore, and it is the fourth largest church in Europe, with its three naves, the external facades in white and green marble and its interiors devoid of furnishings but dominated by a cycle of monumental frescoes created by Giorgio Vasari and Federico Zuccari, between 1572 and 1579.\nThe Florence Cathedral is divided into several parts and one of its strengths is certainly Brunelleschi\'s dome: with its masonry covering, divided into eight segments, the golden ball that supports the cross at the top and the Lantern, placed at the top, completed after the death of the artist.\nThen we have the wonderful Giotto\'s Bell Tower, considered the most beautiful bell tower in Italy, from the top of which you can enjoy a wonderful view of the whole of Florence. With a height of almost 85 m, it represents the Florentine Gothic architecture of the 14th century. The bell tower project was started in 1334 by Giotto, who unfortunately was unable to see it completed: he died in fact in 1337, when only the first part of the project had been completed! The construction was therefore carried out by Andrea Pisani, following Giotto\'s instructions to the letter.\nFourth and last beauty of the Duomo of Florence: the Baptistery of San Giovanni. Located in front of the entrance to the Cathedral, it is one of the oldest churches in Florence.'
        }
      },
      {
        title: {
          it: 'Piazza della Signoria e Palazzo Vecchio',
          en: 'Piazza della Signoria and Palazzo Vecchio'
        },
        content: {
          it: 'Piazza della Signoria è una delle piazze storiche più note di Firenze. Si trova a pochi passi dal Duomo e vi si trovano la Fontana del Nettuno, la Statua equestre di Cosimo I de\' Medici e la meravigliosa Loggia dei Lanzi, ricca di statue.\nTra tutti gli edifici, spicca sicuramente il Palazzo Vecchio, un esempio di architettura civile trecentesca. Oggi, all\'interno del palazzo si trovano alcuni edifici comunali ed è la residenza del Primo Cittadino di Firenze. Ma qui si trova anche un museo che vi consigliamo assolutamente di visitare, con delle sale contenenti opere di Michelangelo e Donatello e la Torre di Arnolfo, alta 94 m, dalla cui cima si gode di una vista meravigliosa su tutto il capoluogo toscano.',
          en: 'Piazza della Signoria is one of the most famous historic squares in Florence. It is located a few steps from the Duomo and there you will find the Fountain of Neptune, the Equestrian Statue of Cosimo I de\' Medici and the wonderful Loggia dei Lanzi, rich in statues.\nAmong all the buildings, Palazzo Vecchio certainly stands out, an example of 14th century civil architecture. Today, inside the palace there are some municipal buildings and it is the residence of the First Citizen of Florence. But here there is also a museum that we absolutely recommend you visit, with rooms containing works by Michelangelo and Donatello and the Arnolfo Tower, 94 m high, from the top of which you can enjoy a wonderful view of the entire Tuscan capital.'
        }
      },
      {
        title: {
          it: 'Ponte Vecchio',
          en: 'Ponte Vecchio'
        },
        content: {
          it: 'Uno dei simboli di Firenze, il Ponte Vecchio è considerato uno dei ponti più belli al mondo. Questo suggestivo ponte attraversa il fiume Arno e collega il centro storico fiorentino con la zona est della città.\nIn passato, il Ponte Vecchio era la sede di macellerie e di botteghe di frutta e verdura, oggi invece qui potrete trovare raffinate botteghe orafe che vendono meravigliosi gioielli.',
          en: 'One of the symbols of Florence, the Ponte Vecchio is considered one of the most beautiful bridges in the world. This suggestive bridge crosses the Arno river and connects the historic center of Florence with the eastern area of the city.\nIn the past, the Ponte Vecchio was the headquarters of butchers and fruit and vegetable shops, while today you can find refined goldsmith shops selling wonderful jewels.'
        }
      },
      {
        title: {
          it: 'Galleria degli Uffizi',
          en: 'Uffizi Gallery'
        },
        content: {
          it: 'Senza dubbio si tratta di uno dei complessi museali più belli d\'Italia e del mondo.\nGli Uffizi sono ospitati all\'interno dell\'edificio costruito su progetto di Giorgio Vasari, vicino a Piazza della Signoria. Al suo interno sono esposte prestigiose collezioni d\'arte che vanno dal Medioevo all\'età moderna e potrete vedere alcune delle opere più conosciute al mondo, come la Nascita di Venere e la Primavera del Botticelli e opere di altri illustri artisti.',
          en: 'Without a doubt it is one of the most beautiful museum complexes in Italy and in the world.\nThe Uffizi are housed inside the building built on a project by Giorgio Vasari, near Piazza della Signoria. Inside, prestigious art collections are exhibited ranging from the Middle Ages to the modern age and you will be able to see some of the most famous works in the world, such as the Birth of Venus and Spring by Botticelli and works by other illustrious artists.'
        }
      },
      {
        title: {
          it: 'Piazzale Michelangelo',
          en: 'Piazzale Michelangelo'
        },
        content: {
          it: 'Questo è il miglior punto panoramico per ammirare la città e ospita le copie delle opere più importanti dello omonimo scultore rinascimentale: Il David e le quattro allegorie delle Cappelle Medicee di San Lorenzo realizzate in bronzo.',
          en: 'This is the best panoramic point to admire the city and houses copies of the most important works of the Renaissance sculptor of the same name: The David and the four allegories of the Medici Chapels of San Lorenzo made in bronze.'
        }
      },
      {
        title: {
          it: 'Giardino delle Rose',
          en: 'Rose Garden'
        },
        content: {
          it: 'Proprio attaccato a Piazzale Michelangelo si trova il Giardino delle Rose, un giardino poco conosciuto ma che merita assolutamente una visita.',
          en: 'Right next to Piazzale Michelangelo is the Rose Garden, a little-known garden that absolutely deserves a visit.'
        }
      },
      {
        title: {
          it: 'Palazzo Pitti',
          en: 'Pitti Palace'
        },
        content: {
          it: 'Il complesso della Galleria degli Uffizi non si limita però solo agli Uffizi, ma comprende anche Palazzo Pitti e il Giardino dei Boboli, situati sulla sponda opposta dell\'Arno. Queste due attrazioni sono collegate tra loro e quindi si prestano bene per essere visitate insieme in una giornata.\nPalazzo Pitti ha una struttura molto complessa e la sua visita vi richiederà un po\' di tempo, ma le opere esposte sono di valore inestimabile.',
          en: 'The Uffizi Gallery complex is not limited only to the Uffizi, but also includes Palazzo Pitti and the Boboli Gardens, located on the opposite bank of the Arno. These two attractions are connected to each other and therefore lend themselves well to being visited together in one day.\nPalazzo Pitti has a very complex structure and your visit will take some time, but the works on display are of inestimable value.'
        }
      },
      {
        title: {
          it: 'Giardino di Boboli',
          en: 'Boboli Gardens'
        },
        content: {
          it: 'Alle spalle di Palazzo Pitti, si trova questo bellissimo giardino, il parco storico di Firenze, in cui potrete concedervi una meravigliosa passeggiata. Venne realizzato per volere di Cosimo I de\' Medici e si estende per ben 45.000 mq. Ci sono 4 diversi ingressi per accedervi, ma se prima avete visitato il Palazzo Pitti allora entrerete dal portone principale in Piazza de\' Pitti. Il giardino è davvero molto esteso e con tante cose da vedere, tra piante, sculture, grotte e fontane.',
          en: 'Behind Palazzo Pitti, you will find this beautiful garden, the historic park of Florence, where you can treat yourself to a wonderful walk. It was created at the behest of Cosimo I de\' Medici and covers an area of 45,000 square meters. There are 4 different entrances to access it, but if you have visited Palazzo Pitti first then you will enter from the main door in Piazza de\' Pitti. The garden is really very extensive and with many things to see, including plants, sculptures, caves and fountains.'
        }
      },
      {
        title: {
          it: 'Cappella Brancacci',
          en: 'Brancacci Chapel'
        },
        content: {
          it: 'Oltre alle Cappelle Medicee, si merita una visita anche la Cappella Brancacci. Situata nel quartiere di San Frediano, si trova all\'interno della Chiesa di Santa Maria del Carmine e rappresenta uno dei più elevati esempi di pittura del Rinascimento.',
          en: 'In addition to the Medici Chapels, the Brancacci Chapel also deserves a visit. Located in the San Frediano district, it is inside the Church of Santa Maria del Carmine and represents one of the highest examples of Renaissance painting.'
        }
      },
      {
        title: {
          it: 'Mercato Centrale',
          en: 'Central Market'
        },
        content: {
          it: 'Il capoluogo della Toscana è metropoli sempre in fermento, grazie soprattutto alle sue strade, ai suoi locali, alle sue piazze. Altro fondamentale punto di ritrovo di turisti ed abitanti sono i mercati, il più famoso dei quali è il Mercato Centrale, risalente al XIX secolo.',
          en: 'The capital of Tuscany is a metropolis always in turmoil, thanks above all to its streets, its premises, its squares. Another fundamental meeting point for tourists and residents are the markets, the most famous of which is the Central Market, dating back to the 19th century.'
        }
      }
    ],
    surroundings: [
      {
        title: {
          it: 'Borghi',
          en: 'Villages'
        },
        content: {
          it: 'Fiesole, Montelupo Fiorentino, Chianti, Mugello, Vinci, Barberino, San Miniato, Montecatini Terme, Certaldo, Montepulciano e molti altri.',
          en: 'Fiesole, Montelupo Fiorentino, Chianti, Mugello, Vinci, Barberino, San Miniato, Montecatini Terme, Certaldo, Montepulciano and many others.'
        }
      },
      {
        title: {
          it: 'Terme',
          en: 'Thermal Baths'
        },
        content: {
          it: 'Gambassi Terme, Montecatini Terme, Bagno Vignoni.',
          en: 'Gambassi Terme, Montecatini Terme, Bagno Vignoni.'
        }
      }
    ]
  },
  
  certaldo: {
    name: 'Certaldo',
    region: 'Toscana',
    image: 'https://images.unsplash.com/photo-1529260830199-42c24126f198?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
    description: {
      it: 'Certaldo è una pittoresca cittadina medievale situata nella provincia di Firenze, famosa per essere stata la patria di Giovanni Boccaccio, uno dei più importanti scrittori e poeti italiani del XIV secolo.',
      en: 'Certaldo is a picturesque medieval town located in the province of Florence, famous for being the homeland of Giovanni Boccaccio, one of the most important Italian writers and poets of the 14th century.'
    },
    attractions: [
      {
        title: {
          it: 'Certaldo Alta',
          en: 'Upper Certaldo'
        },
        content: {
          it: 'Certaldo Alta è il cuore storico di questa cittadina, dove si trova la casa museo di Boccaccio. Sempre nel centro storico si trova la Chiesa dei SS. Michele e Jacopo che ospita le spoglie del novelliere.',
          en: 'Upper Certaldo is the historic heart of this town, where Boccaccio\'s house museum is located. Also in the historic center is the Church of SS. Michele and Jacopo which houses the remains of the novelist.'
        }
      },
      {
        title: {
          it: 'Palazzo Pretorio',
          en: 'Praetorian Palace'
        },
        content: {
          it: 'Simbolo della cittadina medievale, dove si trovano alcune importanti opere. Qui si svolgono mostre e numerosi eventi culturali.',
          en: 'Symbol of the medieval town, where some important works are found. Exhibitions and numerous cultural events are held here.'
        }
      },
      {
        title: {
          it: 'Museo di Arte Sacra',
          en: 'Museum of Sacred Art'
        },
        content: {
          it: 'Un importante museo che ospita opere d\'arte sacra di grande valore storico e artistico.',
          en: 'An important museum that houses sacred works of art of great historical and artistic value.'
        }
      },
      {
        title: {
          it: 'Museo del Chiodo',
          en: 'Nail Museum'
        },
        content: {
          it: 'Un museo unico nel suo genere che raccoglie utensili e chiodi di ogni epoca.',
          en: 'A unique museum that collects tools and nails from every era.'
        }
      }
    ],
    surroundings: [
      {
        title: {
          it: 'COSA FARE',
          en: 'WHAT TO DO'
        },
        content: {
          it: 'Certaldo offre il meglio soprattutto nei periodi in cui ospita grandi eventi come il festival Mercantia, che trasforma la cittadina in un luogo magico con le atmosfere del passato. Ma Certaldo è da visitare anche in assenza di eventi, perché offre vie tranquille in cui passeggiare e locali in cui fermarsi per gustare i cibi del territorio.\n\nPoco fuori Certaldo, al Parco Collinare Canonica si possono praticare escursioni e attività come trekking e jogging. Qui potete trovare anche aree picnic e giochi per bambini.',
          en: 'Certaldo offers the best especially in the periods when it hosts big events such as the Mercantia festival, which transforms the town into a magical place with atmospheres of the past. But Certaldo is also worth visiting in the absence of events, because it offers quiet streets in which to walk and places to stop to taste the local food.\n\nJust outside Certaldo, at the Canonica Hill Park you can practice excursions and activities such as trekking and jogging. Here you can also find picnic areas and games for children.'
        }
      },
      {
        title: {
          it: 'ALTRE INFORMAZIONI UTILI',
          en: 'OTHER USEFUL INFORMATION'
        },
        content: {
          it: "Casa del Boccaccio\nVia Boccaccio – Certaldo Alto (Fi)\nTel. 0571.664208 – Fax 0571.664208\nOrario invernale: aperto da martedì a domenica dalle 10:30 alle 13:30\nOrario estivo: aperto tutti i giorni dalle 10:00 alle 19:00\n\nPalazzo Pretorio\nPiazzetta del Vicariato, 1 – Certaldo Alto (Fi)\nTel. 0571.661219\nOrario invernale: aperto da martedì a domenica dalle 10:30 alle 13:30\nOrario estivo: aperto tutti i giorni dalle 10:00 alle 19:00\n\nMuseo d'Arte Sacra\nPiazza SS. Iacopo e Filippo – Certaldo Alto (Fi)\nTel. 0571.661219 Orario: sabato e domenica dalle 10:30 alle 16:30",
          en: "Boccaccio House\nVia Boccaccio – Upper Certaldo (Fi)\nTel. 0571.664208 – Fax 0571.664208\nWinter hours: open from Tuesday to Sunday from 10:30 to 13:30\nSummer hours: open every day from 10:00 to 19:00\n\nPraetorian Palace\nPiazzetta del Vicariato, 1 – Upper Certaldo (Fi)\nTel. 0571.661219\nWinter hours: open from Tuesday to Sunday from 10:30 to 13:30\nSummer hours: open every day from 10:00 to 19:00\n\nSacred Art Museum\nPiazza SS. Iacopo e Filippo – Upper Certaldo (Fi)\nTel. 0571.661219 Hours: Saturday and Sunday from 10:30 to 16:30"
        }
      }
    ]
  },
  
  siena: {
    name: 'Siena',
    region: 'Toscana',
    image: 'https://images.unsplash.com/photo-1537799943037-f5da89a65689?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
    description: {
      it: 'Siena, città della Toscana, nel Centro Italia, si distingue per i suoi edifici medievali in mattoni. Piazza del Campo, la piazza centrale dalla caratteristica forma a ventaglio, è sede del Palazzo Pubblico (il municipio in stile gotico) e della Torre del Mangia, una torre del XIV secolo dalla cui cima si può ammirare una vista mozzafiato sulla città e sulle campagne circostanti. Le diciassette storiche contrade (i quartieri cittadini) si estendono proprio a partire dalla piazza.',
      en: 'Siena, a city in Tuscany in central Italy, is known for its medieval brick buildings. Piazza del Campo, the central square with its characteristic fan shape, is home to the Palazzo Pubblico (the Gothic-style town hall) and the Torre del Mangia, a 14th-century tower from the top of which you can admire a breathtaking view of the city and the surrounding countryside. The seventeen historic districts (the city districts) extend from the square.'
    },
    attractions: [
      {
        title: {
          it: 'Piazza del Campo, Palazzo Pubblico, Torre del Mangia e Museo Civico',
          en: 'Piazza del Campo, Palazzo Pubblico, Torre del Mangia and Civic Museum'
        },
        content: {
          it: 'Dal 1300 Piazza del Campo la sede del famoso Palio, che si tiene a Siena due volte all\'anno, a luglio e ad agosto. Il Palazzo Pubblico ospita anche il Museo Civico, uno dei musei più importanti d\'Italia, che custodisce capolavori importanti dell\'arte toscana.',
          en: 'Since 1300, Piazza del Campo has been the site of the famous Palio, which takes place in Siena twice a year, in July and August. The Palazzo Pubblico also houses the Civic Museum, one of the most important museums in Italy, which houses important masterpieces of Tuscan art.'
        }
      }
    ],
    surroundings: [
      {
        title: {
          it: 'Borghi',
          en: 'Villages'
        },
        content: {
          it: 'Piccoli borghi medievali compaiono in mezzo alle colline che circondano Siena. Tra questi troviamo San Gimignano, Pienza, Montepulciano, Bagno Vignoni, Montalcino, Monteriggioni, San Galgano e molti altri. Pare sempre che quello che si è appena visitato sia il più bello e perfetto, salvo poi ricredersi alla prossima meta.',
          en: 'Small medieval villages appear in the middle of the hills surrounding Siena. Among these we find San Gimignano, Pienza, Montepulciano, Bagno Vignoni, Montalcino, Monteriggioni, San Galgano and many others. It always seems that the one just visited is the most beautiful and perfect, except then changing your mind at the next destination.'
        }
      },
      {
        title: {
          it: 'Castelli',
          en: 'Castles'
        },
        content: {
          it: 'In provincia di Siena, non mancano certo i castelli da vedere. Ecco quelli più noti: il Castello di Briolo, immerso nella splendida campagna del Chianti meridionale; Castello della Chiocciola, che offre splendidi panorami; Castello di Belcaro, nascosto in un foltissimo bosco di lecci; Castello di Meleto, per gli amanti del vino rosso.',
          en: 'In the province of Siena, there is certainly no shortage of castles to see. Here are the most famous ones: Castello di Briolo, immersed in the splendid countryside of southern Chianti; Castello della Chiocciola, which offers splendid views; Castello di Belcaro, hidden in a very dense holm oak forest; Castello di Meleto, for red wine lovers.'
        }
      },
      {
        title: {
          it: 'Terme',
          en: 'Thermal Baths'
        },
        content: {
          it: 'Bagno Vignoni è una stazione termale di origine medioevale; Bagni di San Filippo, piccolo centro termale alle pendici del Monte Amiata; Rapolano Terme, che ospita due dei più importanti stabilimenti termali della Toscana.',
          en: 'Bagno Vignoni is a spa resort of medieval origin; Bagni di San Filippo, a small spa center on the slopes of Mount Amiata; Rapolano Terme, which hosts two of the most important spa establishments in Tuscany.'
        }
      }
    ]
  },
  
  'forte-dei-marmi': {
    name: 'Forte dei Marmi',
    region: 'Toscana',
    image: 'https://images.unsplash.com/photo-1596394723269-b2cbca4e6e33?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
    description: {
      it: 'Forte dei Marmi è uno dei luoghi più noti della costa toscana, più precisamente della Versilia. Questa località offre mare, numerose attività, movida estiva e una gustosa cucina a base di pesce. Forte dei Marmi è molto frequentata non solo da giovani ma anche da personaggi famosi.',
      en: 'Forte dei Marmi is one of the most famous places on the Tuscan coast, more precisely of Versilia. This location offers sea, numerous activities, summer nightlife and a tasty fish-based cuisine. Forte dei Marmi is very popular not only with young people but also with celebrities.'
    },
    attractions: [
      {
        title: {
          it: 'Forte Lorenese',
          en: 'Lorenese Fortress'
        },
        content: {
          it: 'Chiamato anche Fortino, è il simbolo per eccellenza della città e si trova in Piazza Garibaldi, nel cuore del centro storico. Il nome Forte dei Marmi deriva proprio da questo fortino, che compare anche nello stemma della città. Questo edificio fu costruito a scopo difensivo verso la fine del Settecento, per volere del Granduca di Toscana Leopoldo I. Oggi è diventato un luogo culturale, dove si tengono manifestazioni, mostre ed eventi.',
          en: 'Also called Fortino, it is the quintessential symbol of the city and is located in Piazza Garibaldi, in the heart of the historic center. The name Forte dei Marmi derives precisely from this fort, which also appears in the city\'s coat of arms. This building was built for defensive purposes towards the end of the eighteenth century, at the behest of the Grand Duke of Tuscany Leopold I. Today it has become a cultural place, where events, exhibitions and events are held.'
        }
      },
      {
        title: {
          it: 'Piazza Marconi e Mercato di Forte dei Marmi',
          en: 'Piazza Marconi and Forte dei Marmi Market'
        },
        content: {
          it: 'Passeggiando nel centro storico è da visitare la Piazza Marconi, uno dei punti più frequentati e caratteristici della città. Si trova a pochi passi dal Forte Lorenese e dal lungomare, ed è circondata da locali e negozi.\nPiazza Marconi è famosa soprattutto per ospitare il mercato di Forte dei Marmi, oltre a numerosi eventi e manifestazioni, soprattutto nella bella stagione. Si tratta di un mercato che si tiene tutte le settimane, la domenica mattina.',
          en: 'Walking in the historic center, Piazza Marconi is a must-visit, one of the most frequented and characteristic points of the city. It is located a few steps from the Lorenese Fort and the seafront, and is surrounded by bars and shops.\nPiazza Marconi is famous above all for hosting the Forte dei Marmi market, as well as numerous events and exhibitions, especially in the summer. It is a market that is held every week, on Sunday morning.'
        }
      },
      {
        title: {
          it: 'Spiagge',
          en: 'Beaches'
        },
        content: {
          it: 'Forte dei Marmi è una delle località balneari più famose della Versilia e del Tirreno. Scelta anche da molti personaggi famosi, si presenta con ampie spiagge, mare cristallino e stabilimenti balneari attrezzati.\nUno dei principali punti di riferimento più noti è il Pontile di Forte dei Marmi, da cui ammirare splendidi tramonti.',
          en: 'Forte dei Marmi is one of the most famous seaside resorts in Versilia and the Tyrrhenian Sea. Also chosen by many celebrities, it presents itself with wide beaches, crystal clear sea and equipped bathing establishments.\nOne of the main and most famous landmarks is the Forte dei Marmi Pier, from which to admire splendid sunsets.'
        }
      },
      {
        title: {
          it: 'Chiesa Parrocchiale di Sant\'Ermete',
          en: 'Parish Church of Sant\'Ermete'
        },
        content: {
          it: 'Nel cuore del centro storico, a pochi passi dal Forte Lorenese, si trova il principale luogo di culto cattolico di Forte dei Marmi: la Chiesa Parrocchiale di Sant\'Ermete.',
          en: 'In the heart of the historic center, a few steps from the Lorenese Fort, is the main Catholic place of worship in Forte dei Marmi: the Parish Church of Sant\'Ermete.'
        }
      },
      {
        title: {
          it: 'Quartiere di Vittoria Apuana',
          en: 'Vittoria Apuana District'
        },
        content: {
          it: 'Imperdibile una visita a questo quartiere elegante, che ospita numerose ville storiche e viali alberati. Sempre in questo quartiere si trova anche il museo di Ugo Guidi, scultore italiano del Novecento nato a Pietrasanta che morì a Forte dei Marmi.',
          en: 'A visit to this elegant district, which houses numerous historic villas and tree-lined avenues, is a must. Also in this district is the museum of Ugo Guidi, an Italian sculptor of the twentieth century born in Pietrasanta who died in Forte dei Marmi.'
        }
      },
      {
        title: {
          it: 'Oasi WWF Dune Forte dei Marmi',
          en: 'WWF Dunes Oasis Forte dei Marmi'
        },
        content: {
          it: 'Procedendo oltre il quartiere di Vittoria Apuana e seguendo il lungomare, è possibile raggiungere l\'Oasi WWF Dune di Forte dei Marmi, una meravigliosa oasi naturalistica protetta dal WWF. La zona è sempre accessibile, ma è possibile partecipare anche a visite guidate.',
          en: 'Proceeding beyond the Vittoria Apuana district and following the seafront, it is possible to reach the WWF Dunes Oasis of Forte dei Marmi, a wonderful nature oasis protected by the WWF. The area is always accessible, but it is also possible to participate in guided tours.'
        }
      }
    ],
    surroundings: []
  },
  
  viareggio: {
    name: 'Viareggio',
    region: 'Toscana',
    image: 'https://images.unsplash.com/photo-1568822617270-2c1579f8dfe2?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
    description: {
      it: 'Viareggio è una città situata sulla costa toscana. Si tratta di una meta dalle mille risorse artistiche che vanno dall\'arte più antica fino all\'arte contemporanea. La tradizione del Carnevale si manifesta durante la famosa sfilata, ma anche nei musei dedicati a questa festa.',
      en: 'Viareggio is a city located on the Tuscan coast. It is a destination with a thousand artistic resources ranging from the most ancient art to contemporary art. The tradition of Carnival manifests itself during the famous parade, but also in the museums dedicated to this celebration.'
    },
    attractions: [
      {
        title: {
          it: 'Passeggiata Margherita',
          en: 'Margherita Promenade'
        },
        content: {
          it: 'Si tratta del Lungomare di Viareggio, ed è famoso soprattutto per essere il luogo in cui si svolge il famoso Carnevale. Viene chiamata passeggiata perché non costeggia solo la spiaggia come un normale Lungomare, ma dall\'altro lato della strada si estende una vera e propria via artistica in cui possiamo ammirare edifici storici in stile Art Nouveau.',
          en: 'This is the Viareggio Seafront, and it is famous above all for being the place where the famous Carnival takes place. It is called a promenade because it does not only run along the beach like a normal seafront, but on the other side of the street extends a real artistic street where we can admire historic buildings in Art Nouveau style.'
        }
      },
      {
        title: {
          it: 'Villa Argentina',
          en: 'Villa Argentina'
        },
        content: {
          it: 'Uno dei molti edifici storici di Viareggio e dimora signorile. Anche Villa Argentina è stata edificata nell\'800 in stile Art Nouveau. Al suo esterno risalta subito il grande giardino mentre all\'interno si aprono saloni decorati con dipinti, colonne, intagli, ceramiche e mosaici. Ogni tanto la villa ospita mostre d\'arte e altri eventi.',
          en: 'One of the many historic buildings in Viareggio and a stately home. Villa Argentina was also built in the 19th century in Art Nouveau style. Outside, the large garden immediately stands out, while inside there are halls decorated with paintings, columns, carvings, ceramics and mosaics. From time to time the villa hosts art exhibitions and other events.'
        }
      },
      {
        title: {
          it: 'Chiesa San Paolino',
          en: 'San Paolino Church'
        },
        content: {
          it: 'Si tratta di una Basilica minore situata nel centro storico. L\'interno della Chiesa è formato da tre navate, un grande organo e un arco trionfale ma risalta soprattutto un mosaico che rappresenta San Paolino e gli angeli su uno sfondo color oro.',
          en: 'This is a minor Basilica located in the historic center. The interior of the Church is formed by three naves, a large organ and a triumphal arch but what stands out above all is a mosaic that represents San Paolino and the angels on a gold-colored background.'
        }
      },
      {
        title: {
          it: 'Palazzo delle Muse e Galleria d\'Arte Moderna e Contemporanea',
          en: 'Palace of the Muses and Gallery of Modern and Contemporary Art'
        },
        content: {
          it: 'Il Palazzo delle Muse è uno dei palazzi storici di Viareggio. Oggi all\'interno del palazzo si sviluppa la Galleria d\'Arte Moderna e Contemporanea nata in seguito al Premio Carnevalotto che premia il carro più artistico. All\'interno del museo si trovano circa 3000 opere d\'arte contemporanea che comprendono pitture, sculture e grafiche, ideate e utilizzate come premio per il carro primo classificato.',
          en: 'The Palace of the Muses is one of the historic buildings of Viareggio. Today inside the building is the Gallery of Modern and Contemporary Art, born following the Carnevalotto Prize which rewards the most artistic carnival float. Inside the museum there are about 3000 works of contemporary art that include paintings, sculptures and graphics, conceived and used as a prize for the first classified float.'
        }
      },
      {
        title: {
          it: 'Cittadella e Museo del Carnevale',
          en: 'Citadel and Carnival Museum'
        },
        content: {
          it: 'Conosciuta anche come Piazza delle Sette Vie, è la seconda piazza per importanza della città. Nel Medioevo era il centro politico della città di Pisa mentre nel Cinquecento divenne sede dei Cavalieri di Santo Stefano. Presenta un aspetto rinascimentale, a seguito degli interventi di Giorgio Vasari, che progettò importanti palazzi che ancora la incorniciano.',
          en: 'Also known as Piazza delle Sette Vie, it is the second most important square in the city. In the Middle Ages it was the political center of the city of Pisa, while in the sixteenth century it became the headquarters of the Knights of Santo Stefano. It has a Renaissance appearance, following the interventions of Giorgio Vasari, who designed important palaces that still frame it.'
        }
      }
    ],
    surroundings: [
      {
        title: {
          it: 'NEI DINTORNI',
          en: 'SURROUNDINGS'
        },
        content: {
          it: 'San Miniato\nVolterra\nLivorno\nLucca',
          en: 'San Miniato\nVolterra\nLivorno\nLucca'
        }
      }
    ]
  },
  
  lucca: {
    name: 'Lucca',
    region: 'Toscana',
    image: 'https://images.unsplash.com/photo-1591796764479-c4408408639f?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
    description: {
      it: 'Lucca è la città medievale per eccellenza e sono tante le cose che vale la pena vedere nella sua provincia, anche perché il territorio spazia tra mare, montagna e vallate incontaminate. Protetta da una possente mura di cinta, è la città della Toscana che ha mantenuto più intatto il suo impianto medievale: all\'interno delle mura troverete chiese, torri e chiostri e palazzi antichi.',
      en: 'Lucca is the medieval city par excellence and there are many things worth seeing in its province, also because the territory ranges from sea, mountains and unspoiled valleys. Protected by powerful walls, it is the city in Tuscany that has kept its medieval layout most intact: inside the walls you will find churches, towers, cloisters and ancient palaces.'
    },
    attractions: [
      {
        title: {
          it: 'Piazza dell\'Anfiteatro',
          en: 'Amphitheater Square'
        },
        content: {
          it: 'La visita alla città non può che iniziare da qui, la bellissima piazza ellittica simbolo della città, Sorge su quello che era un enorme anfiteatro romano i cui resti sono ancora visibili.',
          en: 'A visit to the city can only start from here, the beautiful elliptical square symbol of the city. It stands on what was once a huge Roman amphitheater, whose remains are still visible.'
        }
      },
      {
        title: {
          it: 'Duomo, Cattedrale di San Martino e Campanile',
          en: 'Cathedral of San Martino and Bell Tower'
        },
        content: {
          it: 'Raggiungete il Duomo di Lucca, dove troverete la Cattedrale di San Martino, una meraviglia in stile romanico che vale assolutamente una visita. La torre campanaria del XII secolo con i suoi 60 metri di altezza è la più alta della città e offre un bellissimo panorama di Lucca e del paesaggio circostante.',
          en: 'Visit the Duomo of Lucca, where you will find the Cathedral of San Martino, a Romanesque wonder that is absolutely worth a visit. The 12th century bell tower with its 60 meters height is the highest in the city and offers a beautiful panorama of Lucca and the surrounding landscape.'
        }
      },
      {
        title: {
          it: 'Piazza San Michele',
          en: 'San Michele Square'
        },
        content: {
          it: 'Sui resti di un antico foro romano sorge un\'altra bella piazza di Lucca, quella di San Michele dove si affacciano edifici degni di nota come la Chiesa di San Michele in Foro, Palazzo Gigli e Palazzo Pretorio.',
          en: 'On the remains of an ancient Roman forum stands another beautiful square in Lucca, that of San Michele where notable buildings face each other such as the Church of San Michele in Foro, Palazzo Gigli and Palazzo Pretorio.'
        }
      },
      {
        title: {
          it: 'Piazza Napoleone',
          en: 'Napoleone Square'
        },
        content: {
          it: 'Si tratta della più grande Piazza di Lucca, che sorge sulle ceneri di una antica fortezza. La piazza prende il nome da Elisa Bonaparte che dal 1806 governò il Principato di Lucca e incaricò l\'architetto Lazzarini di creare uno spiazzo che valorizzasse il Palazzo Ducale che oggi è della provincia di Lucca, In questa piazza, ogni estate si tiene l\'appuntamento con il Lucca Summer Festival.',
          en: 'It is the largest square in Lucca, which stands on the ashes of an ancient fortress. The square takes its name from Elisa Bonaparte who ruled the Principality of Lucca from 1806 and commissioned the architect Lazzarini to create a square that would enhance the Ducal Palace that is now the province of Lucca. In this square, every summer there is the appointment with the Lucca Summer Festival.'
        }
      },
      {
        title: {
          it: 'Torre delle Ore e Torre Guinigi',
          en: 'Clock Tower and Guinigi Tower'
        },
        content: {
          it: 'Un tempo a Lucca si contavano oltre 200 torri, oggi potrete visitarne due, sono Torre delle ore che si trova su via Fillungo e Torre Giunigi che è situata all\'angolo tra via Sant\'Andrea e via delle Chiavi D\'Oro.',
          en: 'Once in Lucca there were over 200 towers, today you can visit two, they are the Clock Tower located on via Fillungo and the Guinigi Tower which is located at the corner between via Sant\'Andrea and via delle Chiavi D\'Oro.'
        }
      },
      {
        title: {
          it: 'Lucca Comics and Games',
          en: 'Lucca Comics and Games'
        },
        content: {
          it: 'Ogni anno, tra il mese di ottobre e novembre Lucca ospita il Lucca Comics and Games, che accoglie migliaia di appassionati da tutta Europa: il grande festival dedicato al fumetto, all\'animazione e ai giochi.',
          en: 'Every year, between October and November, Lucca hosts Lucca Comics and Games, which welcomes thousands of enthusiasts from all over Europe: the great festival dedicated to comics, animation and games.'
        }
      }
    ],
    surroundings: [
      {
        title: {
          it: 'NEI DINTORNI',
          en: 'SURROUNDINGS'
        },
        content: {
          it: 'Borgo a Mozzano\nCapannori\nAltopascio\nBarga\nParco fluviale del fiume Serchio\nGarfagnana\nViareggio',
          en: 'Borgo a Mozzano\nCapannori\nAltopascio\nBarga\nSerchio river park\nGarfagnana\nViareggio'
        }
      }
    ]
  },
  
  livorno: {
    name: 'Livorno',
    region: 'Toscana',
    image: 'https://images.unsplash.com/photo-1602075432748-82d264e2b463?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
    description: {
      it: 'La città di Livorno ospita tantissimi luoghi interessanti da vedere, spesso poco conosciuti e tutta la provincia affaccia sul mare.',
      en: 'The city of Livorno hosts many interesting places to see, often little known, and the entire province faces the sea.'
    },
    attractions: [
      {
        title: {
          it: 'Livorno Centro',
          en: 'Livorno City Center'
        },
        content: {
          it: 'Spesso viene considerata una città di passaggio, ma in realtà vale la pena visitarla soffermandosi più a lungo.',
          en: 'It is often considered a city of passage, but it is actually worth visiting it for longer.'
        }
      },
      {
        title: {
          it: 'Terrazza Mascagni e quartiere Venezia',
          en: 'Mascagni Terrace and Venice district'
        },
        content: {
          it: 'Uno dei punti più conosciuti della città è questa terrazza, cioè una grande piazza panoramica che affaccia sul mare. Spostatevi poi nel quartiere conosciuto come la Venezia, così chiamato per i fossi che la solcano e che la rendono così caratteristica, ricorda quindi la città di Venezia.',
          en: 'One of the most well-known points of the city is this terrace, that is a large panoramic square overlooking the sea. Then move to the neighborhood known as Venice, so called for the ditches that furrow it and that make it so characteristic, thus recalling the city of Venice.'
        }
      },
      {
        title: {
          it: 'Mercato Centrale',
          en: 'Central Market'
        },
        content: {
          it: 'Uno dei più bei mercati italiani storici. Qui si trova davvero di tutto: pesce, carne, pane uova e pollerie, bar e banchi adibiti a piccoli ristoranti.',
          en: 'One of the most beautiful historic Italian markets. Here you can really find everything: fish, meat, bread, eggs and poultry, bars and stalls used as small restaurants.'
        }
      }
    ],
    surroundings: []
  },
  
  garfagnana: {
    name: 'Garfagnana',
    region: 'Toscana',
    image: 'https://images.unsplash.com/photo-1633427432636-78f3aad389ff?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
    description: {
      it: 'La Garfagnana costituisce uno spazio verde ricco di paesaggi naturali, vette, sentieri e antichi borghi cittadini racchiusi tra le Alpi Apuane e l\'Appennino Tosco-emiliano. Numerosi sono gli appassionati di trekking, montagna e natura che hanno scelto la Garfagnana come paesaggio naturale per le loro escursioni. Assolutamente da provare i prodotti tipici locali, come farro, castagne, formaggi e funghi.',
      en: 'Garfagnana is a green space rich in natural landscapes, peaks, paths and ancient towns enclosed between the Apuan Alps and the Tuscan-Emilian Apennines. There are many trekking, mountain and nature enthusiasts who have chosen Garfagnana as a natural landscape for their excursions. Absolutely worth trying the local typical products, such as spelled, chestnuts, cheeses and mushrooms.'
    },
    attractions: [
      {
        title: {
          it: 'Castelnuovo di Garfagnana',
          en: 'Castelnuovo di Garfagnana'
        },
        content: {
          it: 'Capoluogo della Garfagnana, distante sia da Lucca che dalla Versilia 40 km circa, e si presenta ricco di monumenti storici come la Rocca Ariostesca, il Duomo Comunale e la Piazza di S. Pietro, e attività commerciali su uno sfondo ancora naturale e selvaggio con grandi scorci panoramici.',
          en: 'Capital of Garfagnana, about 40 km from both Lucca and Versilia, it is rich in historical monuments such as the Rocca Ariostesca, the Municipal Cathedral and the Piazza di S. Pietro, and commercial activities on a background that is still natural and wild with great panoramic views.'
        }
      },
      {
        title: {
          it: 'Parco Naturale dell\'Orecchiella',
          en: 'Orecchiella Natural Park'
        },
        content: {
          it: 'Area protetta dal Corpo Forestale dove si trovano numerosi animali selvatici, come lupo appenninico, l\'aquila reale, capriolo e orso bruno, e varie specie vegetali. Il parco si sviluppa su vari sentieri di diversa difficoltà che permettono una visita interessante a grandi e piccoli.',
          en: 'Protected area by the Forest Corps where numerous wild animals are found, such as Apennine wolf, golden eagle, roe deer and brown bear, and various plant species. The park develops on various paths of different difficulty that allow an interesting visit to adults and children.'
        }
      }
    ],
    surroundings: []
  },
  
  abetone: {
    name: 'Abetone',
    region: 'Toscana',
    image: 'https://images.unsplash.com/photo-1483401757487-2ced3fa77952?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
    description: {
      it: 'Nel cuore verde della Toscana tra le montagne della città di Pistoia, si trova una delle più belle località italiane. L\'Abetone è una meta vacanziera che riscuote gran successo sia in estate che in inverno. Posta nella valle del Sestaione, a 1400 metri sul livello del mare, questa località segna quasi il confine con l\'Emilia Romagna ed è una meta in cui si possono trovare percorsi estivi di trekking e piste sciistiche molto gettonate. Il clima temperato della zona permette di godere di una buona permanenza sia in estate che in inverno, portando ancora una volta il nome del posto tra le più belle località turistiche presenti sul territorio italiano.',
      en: 'In the green heart of Tuscany among the mountains of the city of Pistoia, there is one of the most beautiful Italian locations. Abetone is a holiday destination that is very successful both in summer and winter. Located in the Sestaione valley, at 1400 meters above sea level, this location almost marks the border with Emilia Romagna and is a destination where you can find summer trekking routes and very popular ski slopes. The temperate climate of the area allows you to enjoy a good stay both in summer and winter, bringing once again the name of the place among the most beautiful tourist locations on Italian territory.'
    },
    attractions: [],
    surroundings: [
      {
        title: {
          it: 'Laghi',
          en: 'Lakes'
        },
        content: {
          it: 'Lago Nero\nLago Santo Modenese',
          en: 'Lago Nero\nLago Santo Modenese'
        }
      }
    ]
  },
  
  chiavari: {
    name: 'Chiavari',
    region: 'Liguria',
    image: 'https://images.unsplash.com/photo-1593690204710-f0a7da58be94?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
    description: {
      it: 'Chiavari è una pittoresca cittadina situata sulla Riviera Ligure di Levante, famosa per i suoi eleganti portici e l\'atmosfera autentica.',
      en: 'Chiavari is a picturesque town located on the Ligurian Riviera di Levante, famous for its elegant arcades and authentic atmosphere.'
    },
    attractions: [
      {
        title: {
          it: 'Centro Storico',
          en: 'Historic Center'
        },
        content: {
          it: 'Il centro storico di Chiavari è caratterizzato dai suoi eleganti portici e palazzi storici. Passeggiare per le vie del centro è un\'esperienza affascinante.',
          en: 'The historic center of Chiavari is characterized by its elegant arcades and historic buildings. Walking through the streets of the center is a fascinating experience.'
        }
      },
      {
        title: {
          it: 'Cattedrale di Nostra Signora dell\'Orto',
          en: 'Cathedral of Our Lady of the Garden'
        },
        content: {
          it: 'La cattedrale principale della città, un bell\'esempio di architettura barocca ligure con una facciata imponente.',
          en: 'The main cathedral of the city, a beautiful example of Ligurian Baroque architecture with an imposing facade.'
        }
      }
    ],
    surroundings: [
      {
        title: {
          it: 'COSA FARE',
          en: 'WHAT TO DO'
        },
        content: {
          it: 'Chiavari offre il meglio soprattutto nei periodi in cui ospita eventi locali e mercati. La città è nota per la produzione di mobili in stile chiavarese e per l\'artigianato locale. Le sue spiagge offrono un piacevole relax durante la stagione estiva.\n\nIl lungomare è perfetto per passeggiate panoramiche con vista sul golfo del Tigullio.',
          en: 'Chiavari offers the best especially in periods when it hosts local events and markets. The city is known for the production of Chiavari style furniture and for local crafts. Its beaches offer pleasant relaxation during the summer season.\n\nThe seafront is perfect for panoramic walks with a view of the Gulf of Tigullio.'
        }
      },
      {
        title: {
          it: 'ALTRE INFORMAZIONI UTILI',
          en: 'OTHER USEFUL INFORMATION'
        },
        content: {
          it: "Ufficio Turistico\nCorso Assarotti, 1 – Chiavari\nTel. 0185.325198\nOrario: aperto tutti i giorni dalle 9:00 alle 12:30 e dalle 15:00 alle 18:00\n\nMercato settimanale\nVenerdì mattina nel centro storico\nOre 8:00-13:00",
          en: "Tourist Office\nCorso Assarotti, 1 – Chiavari\nTel. 0185.325198\nHours: open every day from 9:00 to 12:30 and from 15:00 to 18:00\n\nWeekly market\nFriday morning in the historic center\nFrom 8:00 to 13:00"
        }
      }
    ]
  },
  
  manarola: {
    name: 'Manarola',
    region: 'Liguria',
    image: 'https://images.unsplash.com/photo-1578146165547-9789cc765df5?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
    description: {
      it: 'Manarola è uno dei pittoreschi borghi delle Cinque Terre, arroccato sulle scogliere che si affacciano sul Mar Ligure. Con le sue case colorate e i vigneti terrazzati, offre uno dei panorami più suggestivi della riviera ligure.',
      en: 'Manarola is one of the picturesque villages of the Cinque Terre, perched on the cliffs overlooking the Ligurian Sea. With its colorful houses and terraced vineyards, it offers one of the most suggestive panoramas of the Ligurian Riviera.'
    },
    attractions: [
      {
        title: {
          it: 'Borgo di Manarola',
          en: 'Manarola Village'
        },
        content: {
          it: 'Il borgo stesso, con le sue caratteristiche case colorate a picco sul mare, è la principale attrazione. Vale la pena percorrere i suoi vicoli stretti e le scalinate per ammirare scorci suggestivi.',
          en: 'The village itself, with its characteristic colorful houses overlooking the sea, is the main attraction. It is worth walking through its narrow streets and stairways to admire suggestive views.'
        }
      },
      {
        title: {
          it: 'Via dell\'Amore',
          en: 'Path of Love'
        },
        content: {
          it: 'Il celebre sentiero panoramico che collega Manarola a Riomaggiore, parzialmente riaperto dopo lavori di restauro, offre viste mozzafiato sulla costa.',
          en: 'The famous panoramic path that connects Manarola to Riomaggiore, partially reopened after restoration works, offers breathtaking views of the coast.'
        }
      }
    ],
    surroundings: [
      {
        title: {
          it: 'COSA FARE',
          en: 'WHAT TO DO'
        },
        content: {
          it: 'Manarola è famosa per le passeggiate panoramiche e i sentieri escursionistici che la collegano agli altri borghi delle Cinque Terre. Da non perdere il sentiero panoramico che porta al Belvedere, da dove si può ammirare la migliore vista del borgo.\n\nPotete anche degustare il vino locale Sciacchetrà, prodotto dai vigneti terrazzati che circondano il borgo.',
          en: 'Manarola is famous for the panoramic walks and hiking trails that connect it to the other villages of the Cinque Terre. Do not miss the panoramic path that leads to the Belvedere, from where you can admire the best view of the village.\n\nYou can also taste the local Sciacchetrà wine, produced from the terraced vineyards surrounding the village.'
        }
      },
      {
        title: {
          it: 'ALTRE INFORMAZIONI UTILI',
          en: 'OTHER USEFUL INFORMATION'
        },
        content: {
          it: "Centro Visite del Parco Nazionale delle Cinque Terre\nVia Discovolo – Manarola\nTel. 0187.760511\nOrario estivo: tutti i giorni 10:00-19:00\nOrario invernale: 10:00-16:00\n\nInfopoint turistico\nStazione ferroviaria di Manarola\nOrario: 9:00-17:00",
          en: "Visitor Center of the Cinque Terre National Park\nVia Discovolo – Manarola\nTel. 0187.760511\nSummer hours: every day 10:00-19:00\nWinter hours: 10:00-16:00\n\nTourist Infopoint\nManarola Railway Station\nHours: 9:00-17:00"
        }
      }
    ]
  },
  
  genova: {
    name: 'Genova',
    region: 'Liguria',
    image: 'https://images.unsplash.com/photo-1537221244690-375f4b32dea3?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
    description: {
      it: 'Genova, capoluogo della Liguria, è una città portuale con una storia ricca e affascinante. Il suo centro storico è uno dei più grandi d\'Europa e conserva un notevole patrimonio artistico e architettonico.',
      en: 'Genoa, the capital of Liguria, is a port city with a rich and fascinating history. Its historic center is one of the largest in Europe and preserves a remarkable artistic and architectural heritage.'
    },
    attractions: [
      {
        title: {
          it: 'Centro Storico',
          en: 'Historic Center'
        },
        content: {
          it: 'Il centro storico di Genova, con i suoi caruggi (vicoli stretti), è uno dei più grandi d\'Europa. Qui potete ammirare palazzi storici, chiese antiche e numerosi edifici di interesse culturale.',
          en: 'The historic center of Genoa, with its caruggi (narrow alleys), is one of the largest in Europe. Here you can admire historic buildings, ancient churches and numerous buildings of cultural interest.'
        }
      },
      {
        title: {
          it: 'Acquario di Genova',
          en: 'Genoa Aquarium'
        },
        content: {
          it: 'Uno dei più grandi acquari d\'Europa, ospita oltre 12.000 esemplari di 600 specie diverse. Si trova nell\'area del Porto Antico, ristrutturata dall\'architetto Renzo Piano.',
          en: 'One of the largest aquariums in Europe, it houses over 12,000 specimens of 600 different species. It is located in the Old Port area, renovated by the architect Renzo Piano.'
        }
      },
      {
        title: {
          it: 'Palazzo Ducale',
          en: 'Ducal Palace'
        },
        content: {
          it: 'Antica residenza dei dogi genovesi, oggi è un importante centro culturale che ospita mostre ed eventi. La sua architettura imponente domina Piazza De Ferrari.',
          en: 'Ancient residence of the Genoese doges, today it is an important cultural center that hosts exhibitions and events. Its imposing architecture dominates Piazza De Ferrari.'
        }
      }
    ],
    surroundings: [
      {
        title: {
          it: 'COSA FARE',
          en: 'WHAT TO DO'
        },
        content: {
          it: 'Genova offre numerose attività culturali e gastronomiche. Il Porto Antico è stato riqualificato ed è ora un centro di attrazione con caffè, ristoranti e attività ricreative.\n\nLa cucina genovese è famosa per specialità come il pesto, la focaccia e la farinata. Non perdete l\'occasione di assaggiarle nei ristoranti tipici del centro storico.',
          en: 'Genoa offers numerous cultural and gastronomic activities. The Old Port has been redeveloped and is now an attraction center with cafes, restaurants and recreational activities.\n\nThe Genoese cuisine is famous for specialties such as pesto, focaccia and farinata. Do not miss the opportunity to taste them in the typical restaurants of the historic center.'
        }
      },
      {
        title: {
          it: 'ALTRE INFORMAZIONI UTILI',
          en: 'OTHER USEFUL INFORMATION'
        },
        content: {
          it: "Ufficio Informazioni Turistiche IAT\nPiazza De Ferrari\nTel. 010.5572903\nOrario: tutti i giorni 9:00-18:00\n\nAcquario di Genova\nArea Porto Antico – Ponte Spinola\nTel. 010.23451\nOrario: variabile in base alla stagione, generalmente 9:00-20:00",
          en: "Tourist Information Office IAT\nPiazza De Ferrari\nTel. 010.5572903\nHours: every day 9:00-18:00\n\nGenoa Aquarium\nOld Port Area – Ponte Spinola\nTel. 010.23451\nHours: variable according to the season, generally 9:00-20:00"
        }
      }
    ]
  },
  
  rapallo: {
    name: 'Rapallo',
    region: 'Liguria',
    image: 'https://images.unsplash.com/photo-1591806636957-11b805b8cb72?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
    description: 'Rapallo è una delle principali località turistiche della Riviera Ligure di Levante, elegante e accogliente, con un clima mite tutto l\'anno e un bel lungomare ornato da palme.',
    attractions: [
      {
        title: 'Castello sul Mare',
        content: 'Simbolo di Rapallo, questo antico castello del XVI secolo è stato costruito per difendere la città dagli attacchi dei pirati. Sorge su uno scoglio in mare ed è collegato alla terraferma da un piccolo ponte.'
      },
      {
        title: 'Santuario di Nostra Signora di Montallegro',
        content: 'Situato sulla collina sopra Rapallo, questo santuario offre una vista panoramica sulla città e sul Golfo del Tigullio. Si può raggiungere con una funivia che parte dal centro città.'
      }
    ],
    surroundings: [
      {
        title: 'COSA FARE',
        content: 'Rapallo è famosa per il suo bel lungomare dove è piacevole passeggiare in ogni stagione. La città offre diverse spiagge, giardini pubblici e un porto turistico.\n\nNegli ultimi anni si è affermata come meta per gli amanti del golf grazie al prestigioso circolo situato sulle colline circostanti.'
      },
      {
        title: 'ALTRE INFORMAZIONI UTILI',
        content: "Ufficio Informazioni Turistiche\nLungomare Vittorio Veneto, 7\nTel. 0185.230346\nOrario: tutti i giorni 9:00-12:30 e 15:00-18:00\n\nFunivia per Montallegro\nPiazzale Genova\nTel. 0185.52341\nOrario: 9:00-12:00 e 14:00-17:00 (orari variabili in base alla stagione)"
      }
    ]
  },
  
  sanremo: {
    name: 'Sanremo',
    region: 'Liguria',
    image: 'https://images.unsplash.com/photo-1584433305355-9cb71bba16f0?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
    description: {
      it: 'Sanremo, nota come la Città dei Fiori, è una delle località turistiche più famose della Riviera di Ponente. Conosciuta in tutto il mondo per il Festival della Canzone Italiana, offre un clima mite, splendidi giardini e un\'atmosfera elegante.',
      en: 'Sanremo, known as the City of Flowers, is one of the most famous tourist destinations on the Western Riviera. Known worldwide for the Italian Song Festival, it offers a mild climate, splendid gardens and an elegant atmosphere.'
    },
    attractions: [
      {
        title: {
          it: 'Teatro Ariston',
          en: 'Ariston Theatre'
        },
        content: {
          it: 'Sede del celebre Festival di Sanremo, è un simbolo della cultura musicale italiana. Il teatro ospita numerosi eventi durante tutto l\'anno.',
          en: 'Home of the famous Sanremo Festival, it is a symbol of Italian musical culture. The theater hosts numerous events throughout the year.'
        }
      },
      {
        title: {
          it: 'Casinò di Sanremo',
          en: 'Sanremo Casino'
        },
        content: {
          it: 'Costruito nel 1905, è uno dei casinò più antichi d\'Italia. L\'edificio in stile liberty è un importante punto di riferimento nel centro città.',
          en: 'Built in 1905, it is one of the oldest casinos in Italy. The Art Nouveau building is an important landmark in the city center.'
        }
      },
      {
        title: {
          it: 'La Pigna',
          en: 'La Pigna'
        },
        content: {
          it: 'È il nucleo storico medievale di Sanremo, un quartiere caratteristico con vicoli stretti e tortuosi che si inerpicano sulla collina. Qui si possono ammirare case antiche e scorci suggestivi.',
          en: 'It is the medieval historic core of Sanremo, a characteristic neighborhood with narrow and winding alleys that climb up the hill. Here you can admire ancient houses and suggestive views.'
        }
      }
    ],
    surroundings: [
      {
        title: {
          it: 'COSA FARE',
          en: 'WHAT TO DO'
        },
        content: {
          it: 'Sanremo offre molte attività per i visitatori. Il lungomare Imperatrice è ideale per passeggiate panoramiche, mentre i Giardini Regina Elena ospitano numerose specie di piante esotiche.\n\nLa città è anche famosa per lo shopping di lusso e per la sua tradizione gastronomica che unisce specialità liguri e influenze della vicina Francia.',
          en: 'Sanremo offers many activities for visitors. The Imperatrice promenade is ideal for panoramic walks, while the Regina Elena Gardens host numerous species of exotic plants.\n\nThe city is also famous for luxury shopping and for its gastronomic tradition that combines Ligurian specialties and influences from nearby France.'
        }
      },
      {
        title: {
          it: 'ALTRE INFORMAZIONI UTILI',
          en: 'OTHER USEFUL INFORMATION'
        },
        content: {
          it: "Ufficio Informazioni Turistiche\nCorso Cavallotti, 51\nTel. 0184.544222\nOrario: lunedì-sabato 9:00-13:00 e 15:00-18:00\n\nCasinò di Sanremo\nCorso degli Inglesi, 18\nTel. 0184.5951\nOrario: tutti i giorni dalle 14:00 alle 2:00",
          en: "Tourist Information Office\nCorso Cavallotti, 51\nTel. 0184.544222\nHours: Monday-Saturday 9:00-13:00 and 15:00-18:00\n\nSanremo Casino\nCorso degli Inglesi, 18\nTel. 0184.5951\nHours: every day from 14:00 to 2:00"
        }
      }
    ]
  },
  
  'la-spezia': {
    name: 'La Spezia',
    region: 'Liguria',
    image: 'https://images.unsplash.com/photo-1597131628347-c264cf3d4b8c?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
    description: {
      it: 'La Spezia è una vivace città portuale situata all\'estremità orientale della Liguria, circondata da colline e affacciata sul Golfo dei Poeti. Punto di partenza ideale per esplorare le Cinque Terre, è anche una città con un interessante patrimonio culturale e architettonico.',
      en: 'La Spezia is a lively port city located at the eastern end of Liguria, surrounded by hills and overlooking the Gulf of Poets. An ideal starting point for exploring the Cinque Terre, it is also a city with an interesting cultural and architectural heritage.'
    },
    attractions: [
      {
        title: {
          it: 'Museo Tecnico Navale',
          en: 'Naval Technical Museum'
        },
        content: {
          it: 'Uno dei più importanti musei navali d\'Italia, ospita una vasta collezione di modelli di navi, armi e strumenti nautici che raccontano la storia della marineria italiana.',
          en: 'One of the most important naval museums in Italy, it houses a vast collection of ship models, weapons and nautical instruments that tell the history of Italian seamanship.'
        }
      },
      {
        title: {
          it: 'Castello di San Giorgio',
          en: 'Castle of San Giorgio'
        },
        content: {
          it: 'Situato sulla collina che domina la città, questo castello medievale offre una vista panoramica sul Golfo dei Poeti e ospita il Museo Archeologico.',
          en: 'Located on the hill overlooking the city, this medieval castle offers a panoramic view of the Gulf of Poets and houses the Archaeological Museum.'
        }
      },
      {
        title: {
          it: 'Passeggiata Morin',
          en: 'Morin Promenade'
        },
        content: {
          it: 'Il lungomare di La Spezia, da cui si può ammirare il panorama del golfo e da dove partono i battelli per le Cinque Terre e Portovenere.',
          en: 'The La Spezia seafront, from which you can admire the panorama of the gulf and from where boats leave for the Cinque Terre and Portovenere.'
        }
      }
    ],
    surroundings: [
      {
        title: {
          it: 'COSA FARE',
          en: 'WHAT TO DO'
        },
        content: {
          it: 'La Spezia è un ottimo punto di partenza per esplorare le Cinque Terre e Portovenere, entrambi patrimonio UNESCO. I traghetti partono regolarmente dal porto.\n\nLa città è anche nota per il suo mercato del venerdì, uno dei più grandi della Liguria, e per i piatti di pesce tipici della tradizione locale.',
          en: 'La Spezia is an excellent starting point for exploring the Cinque Terre and Portovenere, both UNESCO heritage sites. Ferries depart regularly from the port.\n\nThe city is also known for its Friday market, one of the largest in Liguria, and for the typical fish dishes of the local tradition.'
        }
      },
      {
        title: {
          it: 'ALTRE INFORMAZIONI UTILI',
          en: 'OTHER USEFUL INFORMATION'
        },
        content: {
          it: "Ufficio Informazioni Turistiche\nVia Mazzini, 47\nTel. 0187.770900\nOrario: tutti i giorni 9:00-19:00\n\nBattelli per le Cinque Terre\nPasseggiata Morin\nOrario: servizio frequente da aprile a ottobre, dalle 9:00 alle 18:00",
          en: "Tourist Information Office\nVia Mazzini, 47\nTel. 0187.770900\nHours: every day 9:00-19:00\n\nBoats to the Cinque Terre\nMorin Promenade\nHours: frequent service from April to October, from 9:00 to 18:00"
        }
      }
    ]
  },
  
  monterosso: {
    name: 'Monterosso',
    region: 'Liguria',
    image: 'https://images.unsplash.com/photo-1539187577537-e54cf54ae3fa?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
    description: {
      it: 'Monterosso al Mare è il più grande dei cinque borghi delle Cinque Terre e l\'unico che presenta una lunga spiaggia sabbiosa. Diviso in due parti dal promontorio di San Cristoforo, offre un piacevole mix di atmosfera marinaresca e strutture turistiche.',
      en: 'Monterosso al Mare is the largest of the five villages of the Cinque Terre and the only one with a long sandy beach. Divided into two parts by the promontory of San Cristoforo, it offers a pleasant mix of seafaring atmosphere and tourist facilities.'
    },
    attractions: [
      {
        title: {
          it: 'Spiaggia di Fegina',
          en: 'Fegina Beach'
        },
        content: {
          it: 'La più ampia spiaggia delle Cinque Terre, caratterizzata dalla statua del Gigante (o Nettuno), una scultura in cemento armato alta 14 metri che un tempo decorava Villa Pastine.',
          en: 'The largest beach in the Cinque Terre, characterized by the statue of the Giant (or Neptune), a 14-meter high reinforced concrete sculpture that once decorated Villa Pastine.'
        }
      },
      {
        title: {
          it: 'Chiesa di San Francesco',
          en: 'Church of San Francesco'
        },
        content: {
          it: 'Situata accanto al Convento dei Cappuccini, conserva importanti opere d\'arte tra cui dipinti attribuiti a Van Dyck e Luca Cambiaso, oltre a un crocifisso ligneo del XVII secolo.',
          en: 'Located next to the Capuchin Convent, it preserves important works of art including paintings attributed to Van Dyck and Luca Cambiaso, as well as a 17th century wooden crucifix.'
        }
      },
      {
        title: {
          it: 'Centro Storico',
          en: 'Historic Center'
        },
        content: {
          it: 'Il borgo vecchio di Monterosso, con le sue case colorate, le stradine strette e la Torre Aurora, una torre di difesa del XVI secolo che un tempo faceva parte del sistema difensivo contro gli attacchi dei pirati.',
          en: 'The old village of Monterosso, with its colorful houses, narrow streets and the Aurora Tower, a 16th century defense tower that was once part of the defensive system against pirate attacks.'
        }
      }
    ],
    surroundings: [
      {
        title: {
          it: 'COSA FARE',
          en: 'WHAT TO DO'
        },
        content: {
          it: 'Monterosso offre numerose attività all\'aria aperta: snorkeling nelle acque cristalline, escursioni sui sentieri panoramici che collegano i borghi delle Cinque Terre, e degustazioni di vini e prodotti locali.\n\nNon perdete l\'occasione di assaggiare le acciughe locali, preparate secondo la tradizione, e il vino Sciacchetrà, prodotto dai vigneti terrazzati della zona.',
          en: 'Monterosso offers numerous outdoor activities: snorkeling in the crystal clear waters, excursions on the panoramic paths that connect the villages of the Cinque Terre, and tastings of local wines and products.\n\nDon\'t miss the opportunity to taste the local anchovies, prepared according to tradition, and the Sciacchetrà wine, produced from the terraced vineyards of the area.'
        }
      },
      {
        title: {
          it: 'ALTRE INFORMAZIONI UTILI',
          en: 'OTHER USEFUL INFORMATION'
        },
        content: {
          it: "Ufficio Informazioni Turistiche\nStazione Ferroviaria di Monterosso\nTel. 0187.817059\nOrario: 9:00-17:00 (orari variabili in base alla stagione)\n\nSentiero Azzurro\nPer informazioni sui sentieri e permessi:\nCentro Visite del Parco Nazionale delle Cinque Terre\nTel. 0187.762600\nOrario: tutti i giorni 9:00-19:00 (estate); 9:00-16:00 (inverno)",
          en: "Tourist Information Office\nMonterosso Train Station\nTel. 0187.817059\nHours: 9:00-17:00 (hours vary according to season)\n\nBlue Path\nFor information on trails and permits:\nVisitor Center of the Cinque Terre National Park\nTel. 0187.762600\nHours: every day 9:00-19:00 (summer); 9:00-16:00 (winter)"
        }
      }
    ]
  },
  
  // Altre città possono essere aggiunte qui seguendo lo stesso formato
};

const HeaderBanner = styled.div`
  background-color: var(--bg-secondary);
  padding: 1.5rem 0;
  text-align: center;
  border-bottom: 1px solid var(--border-color);
`;

const HeaderText = styled.h3`
  font-size: 1.2rem;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: var(--text-secondary);
`;

const BackLink = styled(Link)`
  display: inline-block;
  margin-bottom: 2rem;
  color: var(--text-secondary);
  transition: all 0.3s ease;
  
  &:hover {
    color: var(--accent-primary);
  }
`;

const HeroImage = styled.div<{ $bgImage: string }>`
  width: 100%;
  height: 400px;
  background-image: url(${props => props.$bgImage});
  background-size: cover;
  background-position: center;
  margin-bottom: 3rem;
`;

const CityDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.8;
  margin-bottom: 3rem;
  color: var(--text-primary);
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  margin-bottom: 2rem;
  position: relative;
  display: inline-block;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 60px;
    height: 2px;
    background-color: var(--accent-primary);
  }
`;

const AttractionCard = styled(motion.div)`
  background-color: var(--bg-secondary);
  padding: 2rem;
  margin-bottom: 2rem;
  border: 1px solid var(--border-color);
`;

const AttractionTitle = styled.h3`
  font-size: 1.3rem;
  margin-bottom: 1rem;
  color: var(--accent-primary);
`;

const AttractionContent = styled.p`
  font-size: 1rem;
  line-height: 1.7;
  color: var(--text-secondary);
  white-space: pre-line;
`;

const CityDetail: React.FC = () => {
  const { cityId } = useParams<{ cityId: string }>();
  const cityData = cityId ? cityDatabase[cityId] : null;
  const { t, language } = useLanguage();
  
  if (!cityData) {
    return (
      <Section title={t('city.not_found')} centered>
        <p>{language === 'it' ? 'La città richiesta non è stata trovata.' : 'The requested city was not found.'}</p>
        <BackLink to="/attractions">&lt; {t('city.back_to_attractions')}</BackLink>
      </Section>
    );
  }

  // Funzione di supporto per il rendering dei contenuti multilingua
  const getLocalizedContent = (content: any) => {
    if (content && typeof content === 'object' && content[language]) {
      return content[language];
    }
    return content;
  };
  
  return (
    <>
      <SEOHelmet
        title={`${cityData.name} - ${language === 'it' ? 'Attrazioni e Luoghi da Visitare' : 'Attractions and Places to Visit'}`}
        description={`${language === 'it' ? 'Scopri' : 'Discover'} ${cityData.name}, ${cityData.region}: ${language === 'it' ? 'attrazioni turistiche, cosa vedere e informazioni utili per la tua visita' : 'tourist attractions, what to see and useful information for your visit'}.`}
        canonical={`/citta/${cityId}`}
        keywords={[`${cityData.name.toLowerCase()}`, language === 'it' ? 'attrazioni turistiche' : 'tourist attractions', language === 'it' ? 'luoghi da visitare' : 'places to visit', `${language === 'it' ? 'turismo' : 'tourism'} ${cityData.region.toLowerCase()}`]}
      />
      
      <HeaderBannerComponent />
      
      <Section padding="5rem 0">
        <BackLink to="/attractions">&lt; {t('common.back')}</BackLink>
        
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {cityData.name}
        </motion.h1>
        
        <HeroImage $bgImage={cityData.image} />
        
        <CityDescription>
          {getLocalizedContent(cityData.description)}
        </CityDescription>
        
        <SectionTitle>{t('city.what_to_see')}</SectionTitle>
        
        {cityData.attractions.map((attraction, index) => (
          <AttractionCard
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            <AttractionTitle>
              {getLocalizedContent(attraction.title)}
            </AttractionTitle>
            <AttractionContent>
              {getLocalizedContent(attraction.content)}
            </AttractionContent>
          </AttractionCard>
        ))}
        
        {cityData.surroundings.length > 0 && (
          <>
            <SectionTitle>{t('city.surroundings')}</SectionTitle>
            
            {cityData.surroundings.map((surrounding, index) => (
              <AttractionCard
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <AttractionTitle>
                  {surrounding.title && typeof surrounding.title === 'object'
                    ? surrounding.title[language] 
                    : surrounding.title === 'COSA FARE' 
                      ? t('city.what_to_do')
                      : surrounding.title === 'ALTRE INFORMAZIONI UTILI'
                        ? t('city.useful_info')
                        : surrounding.title}
                </AttractionTitle>
                <AttractionContent>
                  {getLocalizedContent(surrounding.content)}
                </AttractionContent>
              </AttractionCard>
            ))}
          </>
        )}
      </Section>
    </>
  );
};

export default CityDetail; 